// Finds metric quantities in a PDF text run and produces imperial labels for
// the on-page overlay in PdfReader. Values BC framers read in inches get
// framer-native callouts: CSA dressed-lumber pairs (38×89 mm → 2x4) and the
// metricized stud/joist spacings the code uses (400 mm ≈ 16" o.c.). Everything
// is prefixed ≈ — the metric value stays the governing number.

export interface Conversion {
  /** Character range of the metric quantity inside the source string. */
  start: number;
  end: number;
  /** Imperial label to paint, e.g. `≈2x4`, `≈16"`, `≈7'10¼"`, `≈31 psf`. */
  label: string;
}

// CSA dressed size (mm) → nominal inches. 38×89 is a 2x4, 38×235 a 2x10…
const LUMBER: Record<number, number> = {
  19: 1, 38: 2, 64: 3, 89: 4, 114: 5, 140: 6, 184: 8, 235: 10, 286: 12,
};

// The code's metricized layout spacings → the imperial spacing framers set.
const SPACING: Record<number, string> = { 300: '12"', 400: '16"', 600: '24"', 1200: '48"' };

const GLYPH: Record<number, string> = { 2: '⅛', 4: '¼', 6: '⅜', 8: '½', 10: '⅝', 12: '¾', 14: '⅞' };
const frac = (sixteenths: number) => (sixteenths ? GLYPH[sixteenths] ?? `${sixteenths}/16` : '');

/** mm → inch string; long spans come back as feet-inches to the nearest ¼". */
function inches(mm: number): string {
  const totalIn = mm / 25.4;
  if (totalIn >= 48) {
    const q = Math.round(totalIn * 4); // quarter-inches
    const ft = Math.floor(q / 48);
    const rem = q - ft * 48;
    const whole = Math.floor(rem / 4);
    const f = frac((rem % 4) * 4);
    return whole || f ? `${ft}'${whole || ''}${f}"` : `${ft}'`;
  }
  const s = Math.round(totalIn * 16);
  const whole = Math.floor(s / 16);
  const f = frac(s % 16);
  return `${whole || ''}${f}"`;
}

const num = (s: string) => parseFloat(s.replace(/[\s\u00A0,]/g, ''));

export function findConversions(str: string): Conversion[] {
  const out: Conversion[] = [];
  const taken: Array<[number, number]> = [];
  const free = (a: number, b: number) => !taken.some(([x, y]) => a < y && b > x);
  const claim = (a: number, b: number, label: string) => {
    taken.push([a, b]);
    out.push({ start: a, end: b, label });
  };
  let m: RegExpExecArray | null;

  // Lumber pairs first — they must win over the plain-mm pattern
  const pair = /(\d{2,3})\s*[×xX]\s*(\d{2,3})\s*mm\b/g;
  while ((m = pair.exec(str))) {
    const a = LUMBER[+m[1]], b = LUMBER[+m[2]];
    const label = a && b ? `≈${a}x${b}` : `≈${inches(+m[1])}×${inches(+m[2])}`;
    if (free(m.index, m.index + m[0].length)) claim(m.index, m.index + m[0].length, label);
  }

  const mm = /(\d{1,3}(?:[\s\u00A0,]\d{3})+|\d+(?:\.\d+)?)\s*mm\b/g;
  while ((m = mm.exec(str))) {
    const v = num(m[1]);
    if (!isFinite(v) || v < 1 || v > 100000) continue;
    if (!free(m.index, m.index + m[0].length)) continue;
    claim(m.index, m.index + m[0].length, `≈${SPACING[v] ?? inches(v)}`);
  }

  const metres = /(\d+(?:\.\d+)?)\s*m\b(?![mM])/g;
  while ((m = metres.exec(str))) {
    const v = +m[1];
    if (v < 0.1 || v > 1000) continue;
    if (!free(m.index, m.index + m[0].length)) continue;
    claim(m.index, m.index + m[0].length, `≈${inches(v * 1000)}`);
  }

  const rest: Array<[RegExp, (v: number) => string]> = [
    [/(\d+(?:\.\d+)?)\s*kPa\b/g, v => `≈${Math.round(v * 20.885)} psf`],
    [/(\d+(?:\.\d+)?)\s*MPa\b/g, v => `≈${Math.round(v * 145)} psi`],
    [/(\d+(?:\.\d+)?)\s*kN\b/g, v => `≈${Math.round(v * 224.8)} lb`],
    [/(\d+(?:\.\d+)?)\s*kg\b/g, v => `≈${Math.round(v * 2.2046)} lb`],
    [/(-?\d+(?:\.\d+)?)\s*°C/g, v => `≈${Math.round(v * 9 / 5 + 32)}°F`],
  ];
  for (const [re, fmt] of rest) {
    while ((m = re.exec(str))) {
      const v = +m[1];
      if (!isFinite(v)) continue;
      if (!free(m.index, m.index + m[0].length)) continue;
      claim(m.index, m.index + m[0].length, fmt(v));
    }
  }

  return out;
}
